import { Delete } from "lucide-react";
import { Icon } from "../icons/Icon";
import { cx, FOCUS_RING } from "../styles";

/**
 * A digit keypad (DA-10-18, EP10-S18): the recipient's code, and later the
 * cash tendered (EP10-S19), typed with a thumb in 56px keys rather than the
 * phone's keyboard, which covers half the screen and offers letters. The
 * digits typed so far show above the keys; `maxLength` caps them.
 *
 * Western digits whatever the locale: a code is read off an SMS or WhatsApp
 * message, which prints them that way.
 */
export interface NumericPadProps {
	value: string;
	onChange: (next: string) => void;
	maxLength: number;
	/** Names the entered value for a screen reader, e.g. "Code". */
	label: string;
	/** The backspace key's accessible name. */
	deleteLabel: string;
	disabled?: boolean;
}

/** The keys in reading order; `0` sits under `8`, as on a phone. */
const KEYS = ["1", "2", "3", "4", "5", "6", "7", "8", "9", "0", "del"] as const;

export function NumericPad({
	value,
	onChange,
	maxLength,
	label,
	deleteLabel,
	disabled = false,
}: NumericPadProps) {
	return (
		<div className="flex flex-col gap-3">
			<output
				aria-label={label}
				dir="ltr"
				className="min-h-12 rounded-lg border border-border bg-bg-subtle px-3 py-2 text-center font-mono text-[24px] font-bold tracking-[0.4em] text-fg"
			>
				{value.padEnd(maxLength, "·")}
			</output>
			<div className="grid grid-cols-3 gap-2" dir="ltr">
				{KEYS.map((key) => {
					const del = key === "del";
					return (
						<button
							key={key}
							type="button"
							aria-label={del ? deleteLabel : key}
							disabled={disabled || (del ? value === "" : value.length >= maxLength)}
							onClick={() => onChange(del ? value.slice(0, -1) : value + key)}
							className={cx(
								"flex min-h-14 items-center justify-center rounded-lg border border-border bg-bg-surface text-[22px] font-bold text-fg disabled:bg-bg-subtle disabled:text-fg-muted",
								key === "0" && "col-start-2",
								FOCUS_RING,
							)}
						>
							{del ? <Icon as={Delete} size={22} /> : key}
						</button>
					);
				})}
			</div>
		</div>
	);
}
